import { redirect } from "@tanstack/react-router"
import { create } from "zustand"

import type {
  Product,
  ProductUpdateFailure,
  ProductUpdateMutation,
  ProductUpdateMutationVariables,
} from "@/graphql/codegen/graphql"
import { ProductStatusReason, ProductUpdateDocument } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { NotificationType } from "@/schemas/notification"
import type { ProductActionsStore } from "@/screens/Companies/products/types/schema"
import { ProductActions } from "@/screens/Companies/products/types/schema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const deactivateProduct = async (product: Product) => {
  const enqueueNotification = useNotificationsStore.getState().enqueueNotification
  const { data, error } = await queryClient.mutation<ProductUpdateMutation, ProductUpdateMutationVariables>(
    ProductUpdateDocument,
    {
      input: {
        productId: product.productId,
        activityStatusReason: ProductStatusReason.Discontinued,
        isActive: false,
      },
    }
  )

  if (error || data?.product.update.__typename === "ProductUpdateFailure") {
    enqueueNotification({
      type: NotificationType.Error,
      message: error?.message || (data?.product.update as ProductUpdateFailure).error.message,
    })
  }

  if (data) {
    enqueueNotification({
      type: NotificationType.Success,
      message: "Product deactivated successfully",
    })
  }
}

const activateProduct = async (product: Product) => {
  const enqueueNotification = useNotificationsStore.getState().enqueueNotification
  const { data, error } = await queryClient.mutation<ProductUpdateMutation, ProductUpdateMutationVariables>(
    ProductUpdateDocument,
    {
      input: {
        productId: product.productId,
        isActive: true,
        activityStatusReason: ProductStatusReason.Active,
      },
    }
  )

  if (error || data?.product.update.__typename === "ProductUpdateFailure") {
    enqueueNotification({
      type: NotificationType.Error,
      message: error?.message || (data?.product.update as ProductUpdateFailure).error.message,
    })
  }

  if (data) {
    enqueueNotification({
      type: NotificationType.Success,
      message: "Product activated successfully",
    })
  }
}

export const useProductActionsStore = create<ProductActionsStore>((set, get) => ({
  productAtAction: null,
  openedDialog: null,
  setOpenedDialog: (dialog) => set({ openedDialog: dialog }),
  setProductAtAction: (product) => set({ productAtAction: product }),
  loadProductPricing: async (_productId) => {
    // const { data, error } = await queryClient.query<ProductPricingQuery, ProductPricingQueryVariables>(ProductPricingDocument, {
    //   productId,
    // })
  },
  onAction: async (action) => {
    const product = get().productAtAction
    if (!product) {
      console.error("No product selected for action")
      return
    }

    if (action === ProductActions.EDIT_PRODUCT_DETAILS) {
      redirect({ to: `/companies/products/${product.productId}/edit` })
      return
    }

    if (action === ProductActions.DEACTIVATE_PRODUCT) {
      await deactivateProduct(product)
    }

    if (action === ProductActions.ACTIVATE_PRODUCT) {
      await activateProduct(product)
    }
  },
}))
